/*
===============================================================================================================
                                 Copyright 2022-2023, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { LocaleContext } from '@/contexts/locale-context';

/**
 * Component to display uneditable text field with the title.
 * @param {string} inputSelectorId - DOM id, used to identify this element
 * @param {string} title - Displayed above the text field
 * @param {string} value - Text value to be displayed
 * @param {boolean} hiddenIfEmpty - Whether or not it should be rendered when no value provided
 * @param {boolean} boldTitle - Whether or not the title should be bold or the text
 * @param {boolean} fullWidth - Whether or not the field shold take full width
 * @param {boolean} largeFont - Whether or not the text should use large font
 */
export default function TextField({
  inputSelectorId,
  title,
  value,
  hiddenIfEmpty,
  boldTitle,
  fullWidth,
  largeFont,
}) {
  const { textDirection } = useContext(LocaleContext);

  if (hiddenIfEmpty && !value) {
    return null;
  }

  return (
    <div className="text-field" id={inputSelectorId}>
      {title && (
        <div className="title" dir={textDirection}>
          {title}
        </div>
      )}
      <div className={`text${largeFont ? ' large-font' : ''}`} dir={textDirection}>
        {value}
      </div>
      <style jsx>
        {`
          .text-field {
            font-size: var(--font-size-address);
            color: var( --color-title-grey);
          }

          .title {
            margin-bottom: 12px;
            font-family: ${boldTitle ? 'var(--font-family-heading);' : 'inherit'};
          }

          .text {
            color: var(--color-primary-dark);
            font-family: ${boldTitle ? 'inherit' : 'var(--font-family-heading);'};
            max-width: ${fullWidth ? 'unset' : '210px'};
            word-break: break-word;
          }

          .text.large-font {
            font-size: var(--font-size-H1);
            font-weight: var(--font-weight-H1);
            line-height: 1.5;
            margin-bottom: var(--spacing-small);
          }

        `}
      </style>
    </div>
  );
}

TextField.defaultProps = {
  inputSelectorId: null,
  title: null,
  value: '',
  hiddenIfEmpty: true,
  boldTitle: false,
  fullWidth: false,
  largeFont: false,
};

TextField.propTypes = {
  title: PropTypes.string,
  inputSelectorId: PropTypes.string,
  value: PropTypes.string,
  hiddenIfEmpty: PropTypes.bool,
  boldTitle: PropTypes.bool,
  fullWidth: PropTypes.bool,
  largeFont: PropTypes.bool,
};
